import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import type {LegalPageCookiePolicyQuery} from '@/graphql-types'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import LegalCookiePolicyContent from './legal-content-cookie-policy/legal-content-cookie-policy'
import LegalDocumentsGrid from '../legal/documents-grid'

type Props = PageProps<LegalPageCookiePolicyQuery>

export const Head = ({
    location,
    pageContext,
    data,
}: GatsbyHeadProps<LegalPageCookiePolicyQuery>) => {
    const pageData = data.allPrismicLegalPageCookiePolicy.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const LegalPageCookiePolicy = (props: Props) => {
    const pageContent = props.data.allPrismicLegalPageCookiePolicy.nodes[0].data
    const legalDocsContent = props.data.allPrismicLegalDocuments.nodes[0]
    const cookieManagerContent = props.data.allPrismicCookieManager.nodes[0].data

    if (!pageContent || !legalDocsContent || !cookieManagerContent) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />
            <Main>
                {/*
                    <Section>
                        <FeatureBlock
                            title={pageContent.sections[0].section_title}
                            headingLevel="h1"
                            image={pageContent.sections[0].section_image}
                            mirrored={pageContent.sections[0].inverse}
                        >
                            <RichText
                                content={pageContent.sections[0].section_content.richText}
                            />
                        </FeatureBlock>
                    </Section>

                    <ConsentManagerBuilder
                        writeKey={process.env.GATSBY_SEGMENT_WRITE_KEY!}
                        shouldRequireConsent={() => false}
                    >
                        {(consentProps) => (
                            <CookieManager content={cookieManagerContent} {...consentProps} />
                        )}
                    </ConsentManagerBuilder>

                    <Section>
                        <FeatureBlock
                            title={pageContent.sections_part_2[0].section_title}
                            headingLevel="h2"
                            image={pageContent.sections_part_2[0].section_image}
                            mirrored={pageContent.sections_part_2[0].inverse}
                        >
                            <RichText
                                content={
                                    pageContent.sections_part_2[0].section_content.richText
                                }
                            />
                        </FeatureBlock>
                    </Section>
                  */}
                <LegalCookiePolicyContent content={pageContent} />
                <LegalDocumentsGrid content={legalDocsContent} />
            </Main>

            <Footer />
        </>
    )
}

export const query = graphql`
    query LegalPageCookiePolicy($locale: String!) {
        allPrismicLegalPageCookiePolicy(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    sections {
                        section_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        section_title
                        section_content {
                            richText
                        }
                        inverse
                    }
                    used_cookies_title
                    used_cookies_details {
                        used_cookie_title
                        used_cookie_detail {
                            richText
                        }
                    }
                    sections_part_2 {
                        section_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        section_title
                        section_content {
                            richText
                        }
                        inverse
                    }
                }
            }
        }
        allPrismicCookieManager(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    title
                    description {
                        richText
                    }
                    save_preferences_text
                    decline_cookies_text
                    decline_all_cookies_success_message
                    accept_cookies_text
                    accept_all_cookies_success_message
                    customise_text
                    customise_cookies_success_message
                    cookie_name_heading
                    cookie_duration_heading
                    cookie_origin_heading
                    cookie_purpose_heading
                    view_cookies_text
                    hide_cookies_text
                    cookie_categories {
                        necessary_cookies {
                            document {
                                ... on PrismicCookieCategory {
                                    ...CookieDetails
                                }
                            }
                        }
                        performance_cookies {
                            document {
                                ... on PrismicCookieCategory {
                                    ...CookieDetails
                                }
                            }
                        }
                        functional_cookies {
                            document {
                                ... on PrismicCookieCategory {
                                    ...CookieDetails
                                }
                            }
                        }
                        marketing_cookies {
                            document {
                                ... on PrismicCookieCategory {
                                    ...CookieDetails
                                }
                            }
                        }
                    }
                }
            }
        }
        allPrismicLegalDocuments(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                ...LegalDocumentsGrid
            }
        }
    }
`

export default withPrismicPreview(LegalPageCookiePolicy)
